<template>
  <v-hover v-slot="{ hover }">
    <v-card
      class="my-2 pa-3 d-flex align-center justify-space-between"
      outlined
      :class="{ 'red-outline': run.event_id === prevEventId }"
      @click="handlePlay"
      style="cursor: pointer;"
    >
      <div class="d-flex align-center">
        <div>
          <div class="font-weight-bold text-h6">
            {{ (run.total_time ?? 0).toFixed(2) }} s -
            {{ run.player_name }}
            <v-chip v-if="run.distance_from_base" class="ml-2" small color="primary" text-color="white">
              {{ run.distance_from_base }} m
            </v-chip>
            <v-chip class="ml-2" small color="primary" text-color="white">
              {{ baseLabel }}
            </v-chip>
          </div>
          <div class="grey--text text--darken-1 text-caption">
            {{ run.mark_type }} | {{ run.season_state }}
          </div>
        </div>
        <v-chip class="ml-4" x-small color="grey lighten-2" text-color="black">
          Event: {{ run.event_id }}
        </v-chip>
        <v-chip class="ml-4" x-small color="grey lighten-2" text-color="black">
          Is play: {{ run.is_play }}
        </v-chip>
      </div>
      <v-spacer></v-spacer>
      <v-expand-transition>
        <div v-if="hover" class="ml-6 d-flex align-center" style="min-width: 220px;">
          <v-card flat class="pa-2" color="#f9f9f9">
            <div class="text-caption mb-1"><strong>Details</strong></div>
            <div class="text-caption">Game ID: {{ run.game_id }}</div>
            <div class="text-caption">Season ID: {{ run.season_id }}</div>
            <div v-for="item in timeFields" :key="item.key" class="text-caption">
              {{ item.key }}: {{ item.value }}
            </div>
          </v-card>
          <div class="ml-4 d-flex align-center">
            <v-btn icon color="red" @click.stop="deleteRun" :loading="deleting" :disabled="deleting">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-progress-circular
              v-if="deleting"
              indeterminate
              color="red"
              size="20"
              class="ml-2"
            />
          </div>
        </div>
      </v-expand-transition>
      <PlayClip
        v-if="playing"
        :visible="playing"
        :manual_clip="manualClip"
        @close="playing = false"
      />
    </v-card>
  </v-hover>
</template>

<script>
import ax from '@/utils/axios/axios';
import PlayClip from '@/components/video/PlayClip.vue';

export default {
  name: 'RunListItem',
  components: { PlayClip },
  props: {
    run: {
      type: Object,
      required: true
    },
    prevEventId: {
      type: [String, Number],
      default: null
    }
  },
  data: () => ({
    deleting: false,
    playing: false,
    manualClip: null
  }),
  computed: {
    timeFields() {
      return Object.entries(this.run)
        .filter(([key, value]) => key.endsWith('_time') && value !== null)
        .map(([key, value]) => ({ key, value }));
    },
    baseLabel() {
      const mapping = { 1: '1-2', 2: '2-3', 3: '3-K', 0: '1-K' };
      return mapping[this.run.base] ?? this.run.base;
    }
  },
  methods: {
    handlePlay(e) {
      // Prevent play if delete button or loader is clicked
      if (
        e.target.closest('.v-btn') ||
        e.target.closest('.v-progress-circular')
      ) return;
      // Build manual_clip object
      const run = this.run;
      const firstTime = run.first_time_video_time;
      const starttime = Math.max(0, (firstTime - 3));
      const possible = [
        { text: 'Syöttö nousee', key: 'syotto_time' },
        { text: 'Maila laskee', key: 'bat_drops_time' },
        { text: 'Etenijä lähtee', key: 'lahto_time'},
        { text: 'Lyönti osuu mailaan', key: 'hit_time'},
        { text: 'Etenijä pesässä', key: 'runner_at_base_time' },
        { text: 'Pallo hallussa', key: 'possession_time'},
        { text: 'Heitto lähtee', key: 'throw_time' },
        { text: 'Pallo pesässä', key: 'ball_at_base_time' },
      ];
      this.manualClip = {
        starttime: starttime,
        endtime: firstTime + (run.total_time ?? 0) + 3,
        title: run.player_name,
        eventId: run.event_id,
        video_url: run.video_url,
        gameId: run.game_id,
        dataPoints: [],
        firstTime: run.first_time_video_time,
        timenames: possible.map(p => {
          const t = run[p.key];
          return {
            name: p.text,
            time_from_first: t,
            video_time: t !== null ? run.first_time_video_time + t : null
          }
        }).filter(t => !!t.video_time)
      };
      this.playing = true;
    },
    async deleteRun() {
      this.deleting = true;
      try {
        await ax.delete(`/admin/time/${this.run.id}`);
        this.$emit('deleted', this.run.id);
      } catch (e) {
        alert('Failed to delete run');
      } finally {
        this.deleting = false;
      }
    }
  }
}
</script>

<style scoped>
.red-outline {
  border: 2px solid red !important;
}
</style>